// apps/web/src/widgets/freeze-picker/renderer.tsx
//
// 11.6 D2 / Fork-1 Path B — freeze-picker roving radiogroup (copies
// option-tier-matrix a11y; NOT option-set Pill); writes the EXISTING option-set
// {selected} state (no new WidgetState variant); aria-live resolved display =
// recipe-DATA select-implies-display.
//
// The picker is a single-select radiogroup of radio pills over a
// recipe-declared set of styles (config.styles — DATA). Selecting a style writes
// the EXISTING option-set { selected: [styleId] } shape via onStateChange (the
// 11.6-02 type-map resolves StateForTemplate<"freeze-picker"> to that shape — no
// new WidgetState union variant, no 3-site lockstep). Below the group a
// polite live region shows the resolved style's display payload (the
// "select-implies-display" target): the chosen style's label plus its
// display.primary (and optional display.secondary), all recipe DATA.
//
// A11y (LOAD-BEARING — the 11.5 BLOCKER fix, 11.5-UI-REVIEW.md:26,103-109): this
// is a roving-tabindex radiogroup that copies the REVIEW-corrected
// option-tier-matrix pattern (renderer.tsx:215-266). The selected style (or
// config.defaultStyleId, or the first style when none is chosen) is the single
// tab stop; an Arrow key moves SELECTION and SYNCHRONOUSLY moves DOM focus onto
// the destination pill (focusRowAt → parentElement.children[idx].focus()). The
// destination pill already exists in the DOM (only its tabIndex flips on the
// re-render), so the synchronous .focus() is correct and React 19 preserves it
// across the commit. Without it, DOM focus strands on a now-tabIndex={-1} pill.
// The browser test asserts document.activeElement === destinationPill after an
// Arrow press (NOT merely that onStateChange fired — the gap that let the 11.5
// BLOCKER pass CI). Selection is NOT color-only: aria-checked + accent border +
// font-semibold label are the non-color signals.
//
// C1 / META-07 (scripts/grep-no-recipe-identifiers.sh): every style id, label,
// desc, and display string flows from `config` — the renderer NEVER hardcodes a
// freeze/style vocabulary. The only fixed engine string is the generic
// "Pick a freeze style." nothing-chosen hint (UI-SPEC §"User-Facing Strings").
// The display payload uses the schema's domain-free positional roles
// display.primary / display.secondary (the scaling-matrix precedent), never a
// recipe-vocabulary field name or a temp/heat literal.
//
// Sanitization (T-11.6-05-01): all resolved values are JSX text children (React
// auto-escapes); style.desc is a `title` attribute (attribute-escaped). NO
// raw-HTML sink, NO markdown library — the resolved payload is plain
// text. This renderer takes NO new dependency (bespoke buttons like
// option-tier-matrix; the option-set Pill is deliberately NOT used).

import type { FreezeStyle } from "@the-recipe-spread/shared";
import { Card, CardContent } from "@/components/ui/card";
import type { WidgetRendererProps } from "../widget-renderer-props.js";

export function FreezePickerRenderer({
  widget,
  config,
  state,
  onStateChange,
}: WidgetRendererProps<"freeze-picker">): React.ReactElement {
  const styles: ReadonlyArray<FreezeStyle> = config.styles;

  // Resolved selection: the persisted { selected:[id] } first element, else the
  // recipe-declared default style id (the reference defaults to its first
  // style). `undefined` only when neither a selection nor a default exists.
  const chosenId: string | undefined = state?.selected?.[0] ?? config.defaultStyleId;

  // The single roving tab stop: the chosen style, or the first declared style
  // when nothing is chosen (so the radiogroup is always keyboard-reachable).
  const tabStopId: string | undefined = chosenId ?? styles[0]?.id;

  // The resolved style whose display payload drives the aria-live region.
  const resolvedStyle: FreezeStyle | undefined =
    chosenId !== undefined ? styles.find((s) => s.id === chosenId) : undefined;

  const labelId = `${widget.instanceId}-label`;

  // Single-select write: clicking the already-chosen style is a no-op (mirror
  // option-tier-matrix's click-same guard). Immutable whole-state replace.
  const selectStyle = (styleId: string): void => {
    if (chosenId === styleId) return;
    onStateChange({ selected: [styleId] });
  };

  return (
    <Card>
      <CardContent className="p-4">
        <section aria-labelledby={labelId} className="flex flex-col gap-3">
          <h3
            id={labelId}
            className="text-xs font-semibold uppercase tracking-wider text-[var(--recipe-muted)]"
          >
            {config.groupLabel}
          </h3>
          <div role="radiogroup" aria-label={config.groupLabel} className="flex flex-wrap gap-2">
            {styles.map((style, idx) => {
              const isSelected = chosenId === style.id;
              // Roving tabindex: the selected style (or the first style when none
              // is chosen) is the single tab stop per radiogroup.
              const isTabStop = style.id === tabStopId;
              const pillClass = isSelected
                ? "rounded-md border border-[var(--recipe-accent)] bg-[var(--recipe-accent-soft)] px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--recipe-accent)]"
                : "rounded-md border border-[var(--recipe-line)] bg-[var(--recipe-card)] px-3 py-2 text-left transition-colors hover:ring-1 hover:ring-[var(--recipe-accent-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--recipe-accent)]";
              return (
                // biome-ignore lint/a11y/useSemanticElements: custom-styled buttons inside a radiogroup, not native <input type="radio"> — the pill carries a label + a title the native control cannot. Same disposition as option-tier-matrix tiles.
                <button
                  key={style.id}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  tabIndex={isTabStop ? 0 : -1}
                  title={style.desc}
                  onClick={() => {
                    selectStyle(style.id);
                  }}
                  onKeyDown={(e) => {
                    // Standard radiogroup keyboard contract (hand-rolled, no new
                    // dep). Left/Up → previous style; Right/Down → next style;
                    // both move SELECTION (radiogroup semantics). Space/Enter
                    // select the focused style.
                    //
                    // Focus MUST follow the roving tab stop (WAI-ARIA radiogroup
                    // pattern): the re-render after selectStyle flips the
                    // destination pill to tabIndex=0 and this pill to
                    // tabIndex=-1, so DOM focus has to move onto the destination
                    // pill or it strands on a now-untabbable node. The
                    // destination pill is the sibling at the new index within
                    // this radiogroup (e.currentTarget.parentElement holds the
                    // pills in styles[] order). It already exists in the DOM
                    // (only its tabIndex changes on re-render), so a synchronous
                    // .focus() is correct and React 19 preserves it across the
                    // commit. (option-tier-matrix/renderer.tsx:243-265.)
                    const focusStyleAt = (styleIdx: number): void => {
                      const sibling = e.currentTarget.parentElement?.children[styleIdx];
                      if (sibling instanceof HTMLElement) sibling.focus();
                    };
                    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
                      e.preventDefault();
                      const next = styles[idx + 1];
                      if (next !== undefined) {
                        selectStyle(next.id);
                        focusStyleAt(idx + 1);
                      }
                    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
                      e.preventDefault();
                      const prev = styles[idx - 1];
                      if (prev !== undefined) {
                        selectStyle(prev.id);
                        focusStyleAt(idx - 1);
                      }
                    } else if (e.key === " " || e.key === "Enter") {
                      e.preventDefault();
                      selectStyle(style.id);
                    }
                  }}
                  className={pillClass}
                >
                  <span className={`block text-sm ${isSelected ? "font-semibold" : ""}`}>
                    {style.label}
                  </span>
                </button>
              );
            })}
          </div>
          {/* Resolved "select-implies-display" payload in a polite live region so
              a keyboard/screen-reader user hears the resolved value change when
              they change the style. When neither a style nor a default is
              chosen, the generic engine hint shows instead. A resolved style
              whose display payload is missing renders "—" (fail-soft). */}
          <div aria-live="polite" className="flex flex-col gap-1">
            {resolvedStyle !== undefined ? (
              <>
                {/* Lead with the RESOLVED VALUE (display.primary) — the whole
                    point of "select-implies-display". The chosen style's label
                    already renders on the selected pill above, so re-stating it
                    here is redundant; the resolved payload is what changes and
                    what the user came to see, so it gets the emphasis. */}
                <p className="text-sm font-semibold text-[var(--recipe-ink)]">
                  {resolvedStyle.display.primary !== "" ? resolvedStyle.display.primary : "—"}
                </p>
                {resolvedStyle.display.secondary !== undefined ? (
                  <p className="text-sm text-[var(--recipe-muted)]">
                    {resolvedStyle.display.secondary}
                  </p>
                ) : null}
              </>
            ) : (
              <p className="text-sm italic text-[var(--recipe-muted)]">Pick a freeze style.</p>
            )}
          </div>
        </section>
      </CardContent>
    </Card>
  );
}
